'use client';

import { FormEvent, useEffect, useState } from 'react';
import {
  applyVerification,
  getVerificationStatus,
  startConnectOnboarding,
  withdrawVerification,
  type VendorState,
  type VerificationRequest,
} from './api';
import { ConnectSetupChecklist } from './ConnectSetupChecklist';
import { MarketplaceSellerTermsAcceptance } from '@/platform/legal/MarketplaceSellerTermsAcceptance';
import { siteBrandName } from '@/platform/legal/siteLegal';

export interface VendorOnboardingProps {
  onActivated: () => void;
  onApplicationChange?: () => void;
  vendorUserId: string;
}

export function VendorOnboarding({ onActivated, onApplicationChange, vendorUserId }: VendorOnboardingProps) {
  const [state, setState] = useState<VendorState | null>(null);
  const [request, setRequest] = useState<VerificationRequest | null>(null);
  const [canApply, setCanApply] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [existingShopUrls, setExistingShopUrls] = useState('');
  const [error, setError] = useState('');

  function refresh() {
    return getVerificationStatus()
      .then((res) => {
        setState(res.state);
        setRequest(res.latestRequest ?? null);
        setCanApply(res.canApply);
        setBlockReason(res.applyBlockReason ?? '');
        if (res.state.vendorVerificationStatus === 'verified') {
          onActivated();
        }
        onApplicationChange?.();
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function submitApplication(e: FormEvent) {
    e.preventDefault();
    if (!acceptedTerms) {
      setError('Accept the marketplace seller terms to continue.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await applyVerification({
        businessName,
        businessDescription,
        supportEmail,
        existingShopUrls: existingShopUrls
          .split(/[\n,]+/)
          .map((u) => u.trim())
          .filter(Boolean),
        acceptedMarketplaceTerms: true,
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit application');
    } finally {
      setSubmitting(false);
    }
  }

  async function openPaymentSetup() {
    setError('');
    try {
      const { onboardingUrl } = await startConnectOnboarding(vendorUserId);
      window.location.href = onboardingUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start payment setup');
    }
  }

  if (loading) return <p>Loading seller application…</p>;

  const status = state?.vendorVerificationStatus ?? 'none';

  if (status === 'pending') {
    return (
      <section className="vendor-onboarding feed-card settings-page">
        <h2>Application under review</h2>
        <p>Your seller application is pending admin approval. Complete Stripe payment setup while you wait.</p>
        <ConnectSetupChecklist vendorUserId={vendorUserId} />
        <ul className="vendor-onboarding__checklist">
          <li className="done">Application submitted ✓</li>
          <li>Admin approval — pending</li>
        </ul>
        {!state?.connectOnboardingComplete && (
          <button type="button" className="btn btn-primary" onClick={() => void openPaymentSetup()}>
            Complete payment setup
          </button>
        )}
        {request?.rejectionReason && <p className="form-error">{request.rejectionReason}</p>}
      </section>
    );
  }

  if (status === 'rejected') {
    return (
      <section className="vendor-onboarding feed-card settings-page">
        <h2>Application not approved</h2>
        <p>{request?.rejectionReason || 'Your application was not approved. You may re-apply after addressing the issues.'}</p>
        {canApply ? (
          <p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setSubmitting(true);
                setError('');
                withdrawVerification()
                  .then(() => refresh())
                  .catch((err: Error) => setError(err.message))
                  .finally(() => setSubmitting(false));
              }}
              disabled={submitting}
            >
              {submitting ? 'Preparing…' : 'Re-apply'}
            </button>
          </p>
        ) : (
          <p className="text-muted">{blockReason}</p>
        )}
      </section>
    );
  }

  return (
    <section className="vendor-onboarding feed-card settings-page">
      <h2>Apply to sell</h2>
      <p className="text-muted">
        Hosted checkout on {siteBrandName()}. You must pass verification before products can go live.
      </p>
      <ol className="vendor-onboarding__checklist">
        <li className={state?.emailVerified ? 'done' : ''}>Email verified {state?.emailVerified ? '✓' : '—'}</li>
        <li className={state?.termsAccepted ? 'done' : ''}>Site terms accepted {state?.termsAccepted ? '✓' : '—'}</li>
        <li className={state?.profileComplete ? 'done' : ''}>Profile complete {state?.profileComplete ? '✓' : '—'}</li>
      </ol>
      <MarketplaceSellerTermsAcceptance
        id="vendor-apply-marketplace-terms"
        checked={acceptedTerms}
        onChange={setAcceptedTerms}
        disabled={!canApply || submitting}
      />
      {!canApply && blockReason && <p className="form-error">{blockReason}</p>}
      <form onSubmit={(e) => void submitApplication(e)} className="vendor-console__form-grid">
        <label>
          Business / store name
          <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} required disabled={!canApply} />
        </label>
        <label>
          Business description
          <textarea
            value={businessDescription}
            onChange={(e) => setBusinessDescription(e.target.value)}
            rows={3}
            required
            disabled={!canApply}
          />
        </label>
        <label>
          Support email (shown to buyers)
          <input
            type="email"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            required
            disabled={!canApply}
          />
        </label>
        <label>
          Existing shop URLs (optional, one per line)
          <textarea
            value={existingShopUrls}
            onChange={(e) => setExistingShopUrls(e.target.value)}
            rows={2}
            placeholder="https://etsy.com/shop/example"
            disabled={!canApply}
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="vendor-console__submit">
          <button type="submit" className="btn btn-primary" disabled={submitting || !canApply}>
            {submitting ? 'Submitting…' : 'Submit seller application'}
          </button>
        </div>
      </form>
    </section>
  );
}
